/**
 * PaginationDots - Visual indicators for carousel position
 *
 * Features:
 * - First dot shows a plus icon (for "New Trip" card)
 * - Dots highlight when corresponding card is hovered
 * - Active dot is filled white, others are semi-transparent
 */

import { Plus } from "lucide-react";

import { cn } from "@/lib/style";

interface PaginationDotsProps {
  total: number
  current: number
  /** Index of the currently hovered card (-1 if none) */
  hoveredIndex?: number
  onDotClick?: (index: number) => void
  className?: string
}

export function PaginationDots({
  total,
  current,
  hoveredIndex = -1,
  onDotClick,
  className,
}: PaginationDotsProps) {
  if (total <= 1) return null

  return (
    <div
      className={cn('flex justify-center items-center gap-2 mt-3', className)}
      role="tablist"
      aria-label="Trip carousel navigation"
    >
      {Array.from({ length: total }, (_, index) => {
        const isActive = index === current
        const isHovered = index === hoveredIndex
        const isNewTripDot = index === 0

        return (
          <button
            key={index}
            onClick={() => onDotClick?.(index)}
            className={cn(
              'w-[12px] h-[12px] rounded-full flex items-center justify-center',
              'transition-all duration-200 ease-out',
              'hover:scale-150',
              onDotClick && 'cursor-pointer',
              // Active state (current card)
              isActive && 'bg-white',
              // Hovered state (mouse over card)
              !isActive && isHovered && 'bg-white/70',
              // Default state
              !isActive && !isHovered && 'bg-gray-500 hover:bg-gray-400'
            )}
            role="tab"
            aria-selected={isActive}
            aria-label={isNewTripDot ? 'New Trip' : `Trip ${index} of ${total - 1}`}
            tabIndex={isActive ? 0 : -1}
          >
            {isNewTripDot && (
              <Plus
                className={cn(
                  'w-2 h-2',
                  isActive || isHovered ? 'text-gray-900' : 'text-white/70'
                )}
                strokeWidth={2}
              />
            )}
          </button>
        )
      })}
    </div>
  )
}
